import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';

import { LocalItem, LocalVariation } from '~Types';
import {
  CompleteBongQuery_venue_company_layouts as Layout,
  CompleteBongQuery_venue_company_categories_items as Item,
  CompleteBongQuery_venue_company_layouts_categories_page as CategoryPage,
} from '~Scenes/POS/Queries/__queries__';
import { ORDER_STATUS, SERVICE_TYPE } from '~../__queries__/globalTypes';
import { useEntryFinder } from '~Components/EntryFinder';

import ItemButton from './ItemButton';
import SpecialButton from './SpecialButton';
import StagedBundleGrid from './SpecialButton/StagedBundleGrid';
import { generateGrid } from './utils';
import { useWindowSize, useLogger, useI18n } from '~Hooks';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';
import { useInventoryHelpers } from '~Components/InventoryUniverse';
import OutOfStockDimmer from './CategoryOutOfStockDimmer';
import { ItemCategoryMap } from '~Scenes/POS/categories';

const Wrapper = styled.div`
  flex: 1;
  margin-left: -16px;
  overflow: auto;
  position: relative;
  &::-webkit-scrollbar {
    display: none;
  }
`;

export const InnerWrapper = styled.div<{ hasBreadcrumbs: boolean }>`
  flex-direction: row;
  padding-bottom: 104px;
  flex-wrap: wrap;
  flex: 1;
  overflow: auto;
  margin: 4px;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-top: ${({ hasBreadcrumbs }) => (hasBreadcrumbs ? '64px' : '0')};

  &::-webkit-scrollbar {
    display: none;
  }
`;

interface Props {
  layout: Layout | null | undefined;
  venueId: string;
  items: LocalItem[];
  alreadyInCartCountMap?: {
    [itemId: string]: number;
  };
  setSelectedVariationItemId: (value: string | null) => void;
  selectedCategory: string | null;
  categoryItems: {
    id: string;
    itemId: string;
    title: string;
    color: string | undefined;
    vatRate: number | null;
    descriptionLang: any;
    quantity: number | undefined;
    manualPrice: boolean;
    manualName?: boolean | null;
    volumeSuffix: string | null | undefined;
  }[];
  allItems: Item[];
  count: number;
  subPage: CategoryPage | null;
  setSubPage: (val: any) => void;
  mapItem: (item: any) => void;
  addItem: (
    id: string,
    title: string,
    categoryId: any,
    options: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      message?: string | null;
      type?: string;
      count?: number;
      courseType?: any;
      isMessage?: boolean;
      parentId?: string;
      defaultOrderStatus?: ORDER_STATUS | null;
    },
    price?: number,
  ) => { cartItemIds: string[] };
  addItems: (ids: string[]) => void;
  addMessageOnLastItem: (message: string) => void;
  itemToCategoryMap: ItemCategoryMap;
  data: any;
  currentActionId: number;
  breadcrumbs: { id: string; name: string }[];
  serviceType?: SERVICE_TYPE;
  hasServiceCharge?: boolean;
}

const GridButtons = ({
  layout,
  venueId,
  items,
  alreadyInCartCountMap,
  setSelectedVariationItemId,
  selectedCategory,
  categoryItems,
  allItems,
  count,
  subPage,
  setSubPage,
  mapItem,
  addItem,
  addItems,
  addMessageOnLastItem,
  itemToCategoryMap,
  data,
  currentActionId,
  breadcrumbs,
  serviceType,
  hasServiceCharge,
}: Props) => {
  const logger = useLogger('grid-buttons');
  const inventoryHelpers = useInventoryHelpers();
  const [isScrolling, setIsScrolling] = useState(false);

  useEffect(() => {
    if (selectedCategory) {
      setSubPage(null);
    }
  }, [selectedCategory]);

  const findEntryFromItem = useEntryFinder();
  const currentLayout = layout?.categories.find((cat: any) => cat.id == selectedCategory);
  const giftCardsContext = useGiftCardsContext();

  const colNr = subPage ? subPage.columns : currentLayout?.page.columns ?? 3;
  const rows = subPage ? subPage.rows : currentLayout?.page.rows ?? 100;
  const buttonList = subPage ? subPage.buttons : currentLayout?.page.buttons;

  // Here we can filter away items that should never show up in the POS.
  // These can be e.g "utility" items. Implemented to hide GC item in POS.
  const filteredItems = categoryItems.filter((i) => i.itemId !== giftCardsContext.item?.id);

  const { height: windowHeight } = useWindowSize();

  const maxY =
    currentLayout?.page.buttons.reduce((acc, button) => {
      return button.y > acc ? button.y : acc;
    }, 0) ?? 0;

  const usesLayout = layout || subPage;
  const rowsWithoutLayout = Math.ceil(categoryItems.length / colNr);
  const numberOfRows = usesLayout ? maxY + 1 : rowsWithoutLayout;
  const visibleRows = windowHeight < 1024 ? 7 : 9;
  const isScrollable = numberOfRows > visibleRows;

  const grid = !layout
    ? filteredItems
    : buttonList && buttonList.length > 0
    ? generateGrid(colNr, rows).map((square) => {
        const foundButton = buttonList?.find(
          (button: any) => button.x == square.x && button.y == square.y,
        );

        if (foundButton) {
          return {
            ...foundButton,
            id: square.id,
          };
        } else {
          return square;
        }
      })
    : [];

  const handleItemClick = useCallback(
    (id, title, categoryId, options, price) => {
      return addItem(id, title, categoryId, options, price);
    },
    [allItems, count, currentActionId, serviceType],
  );

  const renderItemButton = (button: any, index: number) => {
    const emptySquare = (
      <div
        key={'empty-' + index}
        style={{ width: `calc(${100 / colNr}% - 8px)`, height: '88px', margin: '4px' }}></div>
    );

    if (!button.title.length) {
      return emptySquare;
    } else if (button?.itemId) {
      const foundEntry = findEntryFromItem(button.itemId);
      const foundItem = allItems?.find((it) => it.id == button?.itemId);

      // Don't render items that is not included in menu
      if (!foundEntry || !foundItem) {
        return emptySquare;
      }

      const matchingItem: any = foundItem && mapItem(foundItem);
      const alreadyInCartCount = alreadyInCartCountMap
        ? alreadyInCartCountMap[button.itemId] ?? null
        : null; // TSR function

      return (
        <ItemButton
          key={button.id + button.itemId + button.x + button.y}
          buttonWidth={100 / colNr}
          title={button.title}
          description={matchingItem.descriptionLang}
          imageUrl={matchingItem.imageUrl}
          venueId={venueId}
          onAddItem={handleItemClick}
          isScrolling={isScrolling}
          itemId={button.itemId}
          nrInCart={
            items.filter((item) => item.itemId === button?.itemId)?.length +
            (alreadyInCartCount ?? 0)
          }
          itemToCategoryMap={itemToCategoryMap}
          count={count}
          color={button.color?.length > 0 ? button.color : matchingItem?.color}
          manualPrice={matchingItem?.manualPrice ?? false}
          volumeSuffix={matchingItem?.volumeSuffix ?? null}
          hasServiceCharge={hasServiceCharge}
          price={foundEntry?.price}
        />
      );
    } else {
      return (
        <SpecialButton
          key={button.id + button.title + button.x + button.y}
          button={button}
          buttonWidth={100 / colNr}
          setSubPage={setSubPage}
          isScrolling={isScrolling}
          addMessageOnLastItem={addMessageOnLastItem}
          offers={data?.company.offers ?? []}
          pages={data?.company.pages ?? []}
          addItems={addItems}
        />
      );
    }
  };

  const handlers = useSwipeable({
    delta: isScrollable ? 10 : 100,

    onSwiping: ({ dir }) => {
      // Don't handle scroll if not necessary (unscrollable or side swipes)
      if (dir === 'Left' || dir === 'Right') {
        return;
      }

      setIsScrolling(true);
    },
  });

  let timeout: number;
  const onTouchEnd = () => {
    timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  // Check if subPage is a staged bundle
  const isStagedBundle = subPage && (subPage as any).__stagedBundle;

  return (
    <Wrapper
      className="grid-background"
      data-test="product-items"
      onClick={(e) => {
        if ((e.target as HTMLDivElement).className.includes('grid-background')) {
          setSelectedVariationItemId(null);
        }
      }}>
      {selectedCategory && inventoryHelpers.itemIsOutOfStock(selectedCategory) && (
        <OutOfStockDimmer />
      )}

      {isStagedBundle ? (
        <StagedBundleGrid
          offerName={(subPage as any).offerName}
          stages={(subPage as any).stages}
          categoryItems={categoryItems}
          allItems={allItems}
          venueId={venueId}
          itemToCategoryMap={itemToCategoryMap}
          count={count}
          items={items}
          nrInCartMap={alreadyInCartCountMap || {}}
          onAddItem={handleItemClick}
          onComplete={() => {
            logger.info('Staged bundle completed');
            setSubPage(null); // Go back to main grid
          }}
          hasServiceCharge={hasServiceCharge}
          isScrolling={isScrolling}
        />
      ) : (
        <InnerWrapper
          {...handlers}
          hasBreadcrumbs={breadcrumbs.length > 1}
          onTouchEnd={onTouchEnd}>
          {grid.map(renderItemButton)}
        </InnerWrapper>
      )}
    </Wrapper>
  );
};

export default GridButtons;
